import React, {useState, useContext, Fragment} from 'react';
import styled from 'styled-components';
import {
    Button,
    InputGroup,
    H4,
    H6,
    Icon,
    Divider,
    Collapse,
    MenuItem,
    Menu,
    Slider,
    Popover
} from '@blueprintjs/core';

import {ElemsContext, Area, Curve, StatElems} from '@auravisionlabs/aura-react';
import {errorToast} from '/src/utils/toaster';
import {NORMAL_GRAY_BLUE} from '/src/styles/colors';

const TitleShape = styled.div`
    display: flex;
    align-items: center;

    &:hover {
        cursor: pointer;
    }
`;

const PopOverContainer = styled.div`
    display: block;
    padding: 1em;
`;

const ShapeHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 1rem;
`;

const H6Custom = styled(H6)`
    margin: 0.5em 0 !important;
    margin-left: 1em !important;
`;

const StyledIcon = styled(Icon)`
    margin-left: auto;
`;

const VisibilityIcon = styled(Icon)`
    margin-left: 0.5rem;
`;

const ChangeNameMenu = ({onSave, onCancel, name}) => {
    const [changeShapeNameInput, setChangeShapeNameInput] = useState(name);
    const isButtonDisabled = changeShapeNameInput.length < 1;

    return <PopOverContainer>
        <H6>Change Name</H6>
        <InputGroup
            type="text"
            leftIcon="edit"
            placeholder="New name"
            value={changeShapeNameInput}
            onChange={e => {
                console.log(e.target.value);
                setChangeShapeNameInput(e.target.value);
            }}
            onKeyDown={e => {
                if (e.key === 'Enter') onSave(changeShapeNameInput);
            }}
        />
        <Button
            fill={true}
            text="Change Name"
            intent="primary"
            disabled={isButtonDisabled}
            onClick={() => onSave(changeShapeNameInput)}
        />
    </PopOverContainer>
}

const MenuListElement = (
    {
        elem,
        beforeChange,
        afterChange,
        isOpen,
        handleCurrentMenuOpen,
    }
) => {
    const {elems, selected, setSelected, setElems, setCanvasMode, setHovered} = useContext(ElemsContext);
    const [version, setVersion] = useState(0); // just to re-render on update
    const [changingName, setChangingName] = useState(false); // just to re-render on update

    return (
        <Fragment>
            <div
                onMouseEnter={() => {
                    if (!isOpen || elem === selected) setHovered(elem);
                }}
                onMouseLeave={() => {
                    setHovered(null);
                }}
            >
                <TitleShape onClick={() => handleCurrentMenuOpen(elem)}>
                    <VisibilityIcon
                        icon={elem.visible ? 'eye-open' : 'eye-off'}
                        color={elem.visible ? null : NORMAL_GRAY_BLUE}
                        onClick={(e) => {
                            e.stopPropagation();
                            elem.visible = !elem.visible;
                            setVersion(version + 1);
                            setElems(new StatElems(elems.scale, elems.elems));
                        }}
                    />
                    <ShapeHeader>
                        <Icon
                            icon={Object.getPrototypeOf(elem) === Curve.prototype ? 'flow-linear' : 'polygon-filter'}
                            color={NORMAL_GRAY_BLUE}
                        />
                        <H6Custom>{elem.name}</H6Custom>
                    </ShapeHeader>
                    <StyledIcon icon="caret-down" color={NORMAL_GRAY_BLUE}/>
                </TitleShape>
                <Divider/>
                <Collapse isOpen={isOpen}>
                    <Menu large={true} className="menu-edit-line__SubMenu">
                        <Popover
                            position="auto"
                            isOpen={changingName}
                            onClose={() => setChangingName(false)}
                            content={
                                <ChangeNameMenu
                                    name={elem.name}
                                    onCancel={() => setChangingName(false)}
                                    onSave={(newName) => {
                                        if (elems.elems.map(e => e.name).includes(newName)) {
                                            errorToast({message: `Name ${newName} already exists`});
                                        } else {
                                            beforeChange();  // save history
                                            elem.name = newName;
                                            setVersion(version + 1);
                                            afterChange();  // release changing and update canvas
                                            setChangingName(false);
                                        }
                                    }
                                    }
                                />
                            }
                        >
                            <MenuItem text={`Change name`} icon="edit" onClick={() => setChangingName(!changingName)}/>
                        </Popover>

                        {Object.getPrototypeOf(elem) === Area.prototype && [
                            <MenuItem
                                key={`${elem.name}-area-toggle-ignore`}
                                text={elem.ignore ? 'Make area trigger' : 'Make area ignore'}
                                icon="exchange"
                                onClick={() => {
                                    beforeChange();  // save history
                                    elem.ignore = !elem.ignore;
                                    setVersion(version + 1);
                                    afterChange();  // release changing and update canvas
                                }}
                            />,
                            <MenuItem
                                key={`${elem.name}-area-add-point`}
                                text="Add new point (press p)"
                                icon="new-link"
                                onClick={() => setCanvasMode({ type: 'add-point', area: elem })}
                            />,
                            <MenuItem
                                key={`${elem.name}-area-delete-point`}
                                text="Delete a point (press d)"
                                icon="delete"
                                disabled={elem.poly.length <= 3}
                                onClick={() => setCanvasMode({ type: 'remove-point', area: elem })}
                            />
                        ]}

                        {Object.getPrototypeOf(elem) === Curve.prototype && [
                            <Popover
                                key={`${elem.name}-line-change-cf`}
                                position="auto"
                                fill
                                content={
                                    <PopOverContainer style={{minWidth: 300, padding: '1.5em'}}>
                                        <H6Custom>Change curvature</H6Custom>
                                        <Slider
                                            min={-1}
                                            max={1}
                                            showTrackFill={!!elem.cf}
                                            labelRenderer={(value) => `${Math.round(value * 100)}%`}
                                            labelStepSize={0.5}
                                            value={elem.cf}
                                            stepSize={0.01}
                                            onChange={(value) => {
                                                beforeChange();  // save history
                                                elem.cf = value;
                                                setVersion(version + 1);
                                                setElems(new StatElems(elems.scale, elems.elems));
                                            }}
                                            onRelease={() => {
                                                afterChange(); // release changing and update canvas
                                            }}
                                        />
                                    </PopOverContainer>
                                }
                            >
                                <MenuItem text='Curvature' icon={!!elem.cf ? 'circle' : 'layout-linear'} />
                            </Popover>,
                            <Popover
                                key={`${elem.name}-line-change-vari`}
                                position="auto"
                                fill
                                content={
                                    <PopOverContainer style={{minWidth: 300, padding: '1.5em'}}>
                                        <H6Custom>Change var</H6Custom>
                                        <Slider
                                            min={0}
                                            max={30}
                                            showTrackFill={!!elem.vari}
                                            labelRenderer={(value) => `${value}px`}
                                            labelStepSize={5}
                                            value={elem.vari}
                                            stepSize={1}
                                            onChange={(value) => {
                                                beforeChange();  // save history
                                                elem.vari = value;
                                                setVersion(version + 1);
                                                setElems(new StatElems(elems.scale, elems.elems));
                                            }}
                                            onRelease={() => {
                                                afterChange(); // release changing and update canvas
                                            }}
                                        />
                                    </PopOverContainer>
                                }
                            >
                                <MenuItem text='Variance' icon='bring-data' />
                            </Popover>,
                        ]}
                        <MenuItem
                            text={`Duplicate ${elem.name}`}
                            icon="duplicate"
                            intent="success"
                            onClick={() => {
                                beforeChange();
                                const newElem = elems.copy(elem);
                                setSelected(newElem);
                                setVersion(version + 1);
                                afterChange(); // release changing and update canvas
                            }}
                        />
                        <MenuItem
                            text={`Delete ${elem.name}`}
                            icon="eraser"
                            intent="danger"
                            onClick={() => {
                                if (selected === elem) setSelected(null);
                                beforeChange();
                                elems.remove(elem)
                                setVersion(version + 1);
                                afterChange(); // release changing and update canvas
                            }}
                        />
                    </Menu>
                </Collapse>
            </div>
        </Fragment>
    );
};

export {MenuListElement};
